import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, CloudOff, Loader2 } from 'lucide-react';
import type { EntryBrand, FormAnswers, FormFillState, FormIssue, FormSubmitResult, FormUploadInfo } from '@kaba/shared';
import { BrandLogo } from '../BrandLogo';
import { useAccentScope } from '../../lib/theme';
import { api, ApiError, newOpId } from '../../lib/api';
import { useSerialSave, type SaveFailure, type SerialState } from '../../lib/serialSave';
import { navigate } from '../../lib/router';
import { Button } from '../ui/ui';
import { FormRenderer, type UploadApi } from './FormRenderer';

type Saved = { revision: number; expiresAt: string };

/** Fehler beim Entwurfspeichern einordnen; Revision des neueren Serverstands für „Meine Eingaben behalten“ merken */
export function classifySave(e: unknown, conflictRev: { current: number | null }): SaveFailure {
  if (e instanceof ApiError && e.status === 409) {
    conflictRev.current = (e.data as { details?: { revision?: number } } | undefined)?.details?.revision ?? null;
    return 'conflict';
  }
  if (e instanceof ApiError && e.status === 410) return 'gone';
  return 'offline';
}

/** Speicherzustand des Entwurfs; Konflikte verwerfen nie unbemerkt die eigenen Eingaben */
export function DraftSaveStatus({ state, until, onKeepMine, onLoadOther, onReload, deviceNote }: { state: SerialState<Saved>; until: string | null; onKeepMine: () => void; onLoadOther: () => void; onReload: () => void; deviceNote?: boolean }) {
  const savedUntil = state.kind === 'saved' ? state.result.expiresAt : until;
  if (state.kind === 'saving')
    return (
      <>
        <Loader2 size={12} className="animate-spin" aria-hidden /> Speichere …
      </>
    );
  if (state.kind === 'failed' && state.reason === 'offline')
    return (
      <>
        <CloudOff size={12} aria-hidden /> Nicht gespeichert – keine Verbindung. Deine Eingaben bleiben hier erhalten, wir versuchen es weiter.
      </>
    );
  if (state.kind === 'failed' && state.reason === 'conflict')
    return (
      <span className="flex flex-col gap-1.5 rounded-lg bg-amber-50 p-2.5 text-amber-900 dark:bg-amber-950 dark:text-amber-100" role="alert">
        <span>Dieser Entwurf wurde inzwischen an anderer Stelle gespeichert (z. B. in einem zweiten Fenster). Deine Eingaben hier sind noch nicht gespeichert.</span>
        <span className="flex flex-wrap gap-2">
          <Button size="sm" variant="primary" onClick={onKeepMine}>
            Meine Eingaben speichern
          </Button>
          <Button size="sm" onClick={onLoadOther}>
            Anderen Stand laden …
          </Button>
        </span>
      </span>
    );
  if (state.kind === 'failed' && state.reason === 'gone')
    return (
      <>
        Deine Sitzung ist abgelaufen.{' '}
        <button className="underline" onClick={onReload}>
          Neu laden
        </button>
      </>
    );
  if (state.kind === 'failed') return <>Nicht gespeichert: {state.message}</>;
  return savedUntil ? <>{`Entwurf ${deviceNote ? 'auf diesem Gerät ' : ''}gespeichert bis ${new Date(savedUntil).toLocaleDateString('de-DE')}`}</> : null;
}

/** Hochladen mit Fortschritt (fetch kennt keinen Upload-Fortschritt) */
export function uploadWithProgress(url: string, form: FormData, onProgress: (p: number) => void): Promise<FormUploadInfo> {
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('POST', url);
    x.withCredentials = true;
    x.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    x.onload = () => {
      let data: { error?: string } & FormUploadInfo;
      try {
        data = JSON.parse(x.responseText);
      } catch {
        return reject(new Error(`Fehler ${x.status}`));
      }
      if (x.status >= 200 && x.status < 300) resolve(data);
      else reject(new Error(data.error ?? `Fehler ${x.status}`));
    };
    x.onerror = () => reject(new Error('Keine Verbindung'));
    x.send(form);
  });
}

/** Internes Formular ausfüllen (mit Anmeldung): /f/:id */
export function FormFillPage({ formId }: { formId: string }) {
  const [state, setState] = useState<FormFillState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [uploads, setUploads] = useState<FormUploadInfo[]>([]);
  const [until, setUntil] = useState<string | null>(null);
  const [done, setDone] = useState<FormSubmitResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverIssues, setServerIssues] = useState<FormIssue[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const latest = useRef<FormAnswers>({});
  const attempt = useRef(newOpId());
  const conflictRev = useRef<number | null>(null);
  const sessionId = state?.session?.id;
  const { saver, state: save, setState: setSave } = useSerialSave<FormAnswers, Saved>({
    send: (a, revision) => api.put<Saved>(`/api/form-sessions/${sessionId}`, { answers: a, revision }),
    classify: (e) => classifySave(e, conflictRev),
  });

  const load = useCallback(async () => {
    setError(null);
    try {
      const s = await api.get<FormFillState>(`/api/forms/${formId}/fill`);
      setState(s);
      if (s.session) {
        setAnswers(s.session.answers);
        latest.current = s.session.answers;
        setUploads(s.session.uploads);
        saver.reset(s.session.answers, s.session.revision);
        saver.open();
        setUntil(s.session.expiresAt);
        setSave({ kind: 'idle' });
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }, [formId, saver, setSave]);
  useEffect(() => {
    void load();
  }, [load]);

  const onChange = (a: FormAnswers) => {
    setAnswers(a);
    latest.current = a;
    setServerIssues([]);
    saver.change(a);
  };
  const loadOther = () => {
    if (window.confirm('Den anderen gespeicherten Stand laden? Deine Eingaben hier seit dem letzten Speichern gehen dabei verloren.')) void load();
  };

  const uploadApi: UploadApi = {
    list: uploads,
    upload: async (key, file, onProgress) => {
      const fd = new FormData();
      fd.set('file', file);
      fd.set('key', key);
      const u = await uploadWithProgress(`/api/form-sessions/${state!.session!.id}/uploads`, fd, onProgress);
      setUploads((x) => [...x, u]);
      return u;
    },
    remove: async (id) => {
      await api.del(`/api/form-sessions/${state!.session!.id}/uploads/${id}`);
      setUploads((x) => x.filter((u) => u.id !== id));
    },
    url: (id, thumb) => `/api/form-sessions/${state!.session!.id}/uploads/${id}${thumb ? '?thumb=1' : ''}`,
  };

  if (error)
    return (
      <Shell title="Formular">
        <p role="alert" className="rounded-xl bg-rose-50 p-4 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-100">
          {error}
        </p>
      </Shell>
    );
  if (!state)
    return (
      <Shell title="Formular">
        <p className="text-sm text-[var(--ink-soft)]">Lade …</p>
      </Shell>
    );
  if (done)
    return (
      <Shell title={state.form.title} brand={state.brand}>
        <div className="flex flex-col items-start gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-5 dark:border-emerald-800 dark:bg-emerald-950" role="status">
          <CheckCircle2 size={32} className="text-emerald-600" aria-hidden />
          <p className="text-lg font-semibold">{done.confirmation}</p>
          <p className="text-sm">
            Eingangsnummer: <strong className="tabular-nums">{done.number}</strong>
          </p>
          {done.expectation && <p className="text-sm">{done.expectation}</p>}
          <div className="flex flex-wrap gap-2">
            {done.canOpenCard && done.cardId && (
              <Button variant="primary" onClick={() => navigate(`/b/${done.boardId}?card=${done.cardId}`)}>
                Karte öffnen
              </Button>
            )}
            <Button onClick={() => navigate('/')}>Zur Übersicht</Button>
            <Button
              onClick={() => {
                setDone(null);
                attempt.current = newOpId();
                void load();
              }}
            >
              Weitere Einsendung
            </Button>
          </div>
        </div>
      </Shell>
    );
  if (state.closed || !state.definition || !state.session)
    return (
      <Shell title={state.form.title} brand={state.brand}>
        <p className="rounded-xl bg-[var(--column)] p-4 text-sm">{state.closed ?? 'Dieses Formular ist gerade nicht verfügbar.'}</p>
      </Shell>
    );

  return (
    <Shell title={state.definition.title} brand={state.brand} sub={`Eingang für das Board „${state.form.boardTitle}“`}>
      {state.definition.intro && <p className="text-sm whitespace-pre-line">{state.definition.intro}</p>}
      {state.withdrawn && (
        <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">
          Das Formular wurde geändert. Passende Antworten haben wir übernommen – bitte prüfe sie vor dem Absenden.
        </p>
      )}
      {state.newerVersion && (
        <p className="rounded-lg bg-sky-50 p-3 text-sm text-sky-900 dark:bg-sky-950 dark:text-sky-100">
          Es gibt eine neuere Fassung dieses Formulars. Du kannst diese Einsendung trotzdem abschließen.
        </p>
      )}
      <FormRenderer
        def={state.definition}
        answers={answers}
        onChange={onChange}
        uploads={uploadApi}
        submitting={submitting}
        serverIssues={serverIssues}
        onSubmit={async () => {
          setSubmitting(true);
          setSubmitError(null);
          // Abgesendet wird genau dieser Stand; danach keine Entwurfspeicherung mehr
          saver.close();
          try {
            const r = await api.post<FormSubmitResult>(`/api/form-sessions/${state.session!.id}/submit`, { answers: latest.current, attempt: attempt.current });
            setDone(r);
          } catch (e) {
            // Nie eine erfolgreiche Einsendung behaupten (E01); Entwurf bleibt erhalten
            const d = e instanceof ApiError ? (e.data as { details?: { issues?: FormIssue[] } } | undefined) : undefined;
            if (d?.details?.issues?.length) setServerIssues(d.details.issues);
            setSubmitError(e instanceof ApiError && e.status === 0 ? 'Keine Verbindung. Deine Angaben bleiben erhalten – bitte erneut absenden.' : (e as Error).message);
            saver.open();
            void saver.flush();
          } finally {
            setSubmitting(false);
          }
        }}
      />
      {submitError && (
        <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-100">
          {submitError}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--ink-soft)]">
        <span aria-live="polite" className="inline-flex items-center gap-1.5">
          <DraftSaveStatus state={save} until={until} onKeepMine={() => void saver.overwrite(conflictRev.current ?? saver.revision)} onLoadOther={loadOther} onReload={() => void load()} />
        </span>
        <button
          className="min-h-10 underline"
          onClick={async () => {
            if (!window.confirm('Entwurf verwerfen? Deine Antworten und hochgeladenen Dateien werden gelöscht.')) return;
            saver.close();
            await api.del(`/api/form-sessions/${state.session!.id}`);
            void load();
          }}
        >
          Entwurf verwerfen
        </button>
      </div>
    </Shell>
  );
}

export function Shell({ title, sub, brand, children }: { title: string; sub?: string; brand?: EntryBrand | null; children: React.ReactNode }) {
  useEffect(() => {
    document.title = `${title} – KaBa`;
  }, [title]);
  // Formulare tragen Logo und Farbe ihres Boards (A04)
  useAccentScope(brand ? brand.accent : undefined, !!brand);
  return (
    <div className="min-h-full bg-[var(--surface)]">
      <main className="mx-auto flex max-w-2xl flex-col gap-4 px-4 py-6 sm:py-10">
        <header className="flex flex-col gap-1">
          {brand?.logo && <BrandLogo logo={brand.logo} name="Logo des Boards" size="preview-desktop" className="mb-2 self-start" />}
          <h1 className="text-2xl font-semibold">{title}</h1>
          {sub && <p className="text-sm text-[var(--ink-soft)]">{sub}</p>}
        </header>
        <div className="flex flex-col gap-4 rounded-2xl bg-[var(--panel)] p-4 shadow-sm sm:p-6">{children}</div>
      </main>
    </div>
  );
}
